import { useCallback, useEffect, useState } from 'react';
import {
  Loader,
  AlertCircle,
  Server,
  Plus,
  Pencil,
  Trash2,
  X,
  Check,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { eventTeamApiService } from '../../services/eventTeamApi';
import type { EventServerLink } from '../../types/eventTeam';
import type { EventTeamPerms } from '../../utils/eventTeamPermissions';
import { cn } from '../../utils/cn';

export interface EventServersSectionProps {
  targetType: 'event' | 'project';
  targetId: string;
  perms: EventTeamPerms;
  refreshNonce?: number;
  onChanged?: () => void;
}

/**
 * Server links (guide §1.4): FQDN and/or cluster (one required). Each row can
 * expand into the reverse lookup — other events/projects on the same server.
 */
export const EventServersSection = ({
  targetType,
  targetId,
  perms,
  refreshNonce = 0,
  onChanged,
}: EventServersSectionProps) => {
  const navigate = useNavigate();
  const [links, setLinks] = useState<EventServerLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [showAdd, setShowAdd] = useState(false);
  const [newFqdn, setNewFqdn] = useState('');
  const [newCluster, setNewCluster] = useState('');
  const [newServerName, setNewServerName] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const [adding, setAdding] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editFqdn, setEditFqdn] = useState('');
  const [editCluster, setEditCluster] = useState('');
  const [editServerName, setEditServerName] = useState('');
  const [editNotes, setEditNotes] = useState('');

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [reverse, setReverse] = useState<EventServerLink[] | null>(null);
  const [reverseLoading, setReverseLoading] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const data = await eventTeamApiService.listServerLinks(targetType, targetId);
      setLinks(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load server links');
    } finally {
      setLoading(false);
    }
  }, [targetId, targetType]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);

  useEffect(() => {
    if (refreshNonce === 0) return;
    void load();
  }, [refreshNonce, load]);

  const handleAdd = async () => {
    if (!newFqdn.trim() && !newCluster.trim()) {
      alert('Provide an FQDN or a cluster id (at least one is required).');
      return;
    }
    setAdding(true);
    try {
      await eventTeamApiService.createServerLink(targetType, targetId, {
        fqdn: newFqdn.trim() || null,
        cluster_id: newCluster.trim() || null,
        server_name: newServerName.trim() || null,
        notes: newNotes.trim() || null,
      });
      setNewFqdn('');
      setNewCluster('');
      setNewServerName('');
      setNewNotes('');
      setShowAdd(false);
      await load();
      onChanged?.();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to link server');
    } finally {
      setAdding(false);
    }
  };

  const handleSaveEdit = async (link: EventServerLink) => {
    if (!editFqdn.trim() && !editCluster.trim()) {
      alert('Provide an FQDN or a cluster id (at least one is required).');
      return;
    }
    setBusyId(link.id);
    try {
      await eventTeamApiService.updateServerLink(link.id, {
        fqdn: editFqdn.trim() || null,
        cluster_id: editCluster.trim() || null,
        server_name: editServerName.trim() || null,
        notes: editNotes.trim() || null,
      });
      setEditingId(null);
      await load();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update server link');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (link: EventServerLink) => {
    if (!confirm('Remove this server link?')) return;
    setBusyId(link.id);
    try {
      await eventTeamApiService.deleteServerLink(link.id);
      await load();
      onChanged?.();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete server link');
    } finally {
      setBusyId(null);
    }
  };

  const toggleReverse = async (link: EventServerLink) => {
    if (expandedId === link.id) {
      setExpandedId(null);
      setReverse(null);
      return;
    }
    setExpandedId(link.id);
    setReverse(null);
    setReverseLoading(true);
    try {
      const results = link.fqdn
        ? await eventTeamApiService.getServerLinksByFqdn(link.fqdn)
        : link.cluster_id
          ? await eventTeamApiService.getServerLinksByCluster(link.cluster_id)
          : [];
      setReverse(results);
    } catch {
      setReverse([]);
    } finally {
      setReverseLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader className="w-6 h-6 animate-spin text-accent-400" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="text-sm font-semibold text-glass-300 inline-flex items-center gap-2">
          <Server className="w-4 h-4 text-blue-400" />
          Servers ({links.length})
        </h3>
        {perms.canCreate && (
          <button onClick={() => setShowAdd(!showAdd)} className="glass-button text-xs inline-flex items-center gap-1.5">
            <Plus className="w-3.5 h-3.5" />
            Link server
          </button>
        )}
      </div>

      {error && (
        <div className="glass-card p-3 border-l-4 border-red-400 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-red-400" />
          <span className="text-red-300 text-sm">{error}</span>
        </div>
      )}

      {showAdd && (
        <div className="glass-card p-3 space-y-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <input
              type="text"
              value={newFqdn}
              onChange={(e) => setNewFqdn(e.target.value)}
              placeholder="FQDN (e.g. events.playcdu.co)"
              className="glass-input text-sm"
              autoFocus
            />
            <input
              type="text"
              value={newCluster}
              onChange={(e) => setNewCluster(e.target.value)}
              placeholder="Cluster id"
              className="glass-input text-sm"
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <input
              type="text"
              value={newServerName}
              onChange={(e) => setNewServerName(e.target.value)}
              placeholder="Server name (optional)"
              className="glass-input text-sm w-44"
            />
            <input
              type="text"
              value={newNotes}
              onChange={(e) => setNewNotes(e.target.value)}
              placeholder="Notes (optional)"
              className="glass-input text-sm flex-1 min-w-[160px]"
            />
            <button onClick={() => setShowAdd(false)} className="glass-button-secondary text-xs">
              Cancel
            </button>
            <button
              onClick={() => void handleAdd()}
              disabled={adding || (!newFqdn.trim() && !newCluster.trim())}
              className="glass-button text-xs inline-flex items-center gap-1.5"
            >
              {adding ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              Link
            </button>
          </div>
          <p className="text-[10px] text-glass-500">At least one of FQDN / cluster id is required.</p>
        </div>
      )}

      {links.length === 0 && !error ? (
        <p className="text-glass-400 text-sm py-4 text-center">No servers linked.</p>
      ) : (
        <div className="space-y-1.5">
          {links.map((link) => {
            const busy = busyId === link.id;
            const expanded = expandedId === link.id;
            const isEditing = editingId === link.id;
            return (
              <div key={link.id} className="rounded-lg bg-glass-100/5 border border-glass-200/10">
                {isEditing ? (
                  <div className="p-3 space-y-2">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <input
                        type="text"
                        value={editFqdn}
                        onChange={(e) => setEditFqdn(e.target.value)}
                        placeholder="FQDN"
                        className="glass-input text-sm"
                      />
                      <input
                        type="text"
                        value={editCluster}
                        onChange={(e) => setEditCluster(e.target.value)}
                        placeholder="Cluster id"
                        className="glass-input text-sm"
                      />
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <input
                        type="text"
                        value={editServerName}
                        onChange={(e) => setEditServerName(e.target.value)}
                        placeholder="Server name"
                        className="glass-input text-sm w-44"
                      />
                      <input
                        type="text"
                        value={editNotes}
                        onChange={(e) => setEditNotes(e.target.value)}
                        placeholder="Notes"
                        className="glass-input text-sm flex-1 min-w-[140px]"
                      />
                      <button onClick={() => setEditingId(null)} className="glass-button-secondary !p-1.5">
                        <X className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => void handleSaveEdit(link)}
                        disabled={busy}
                        className="glass-button !p-1.5"
                      >
                        {busy ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-2 px-3 py-2 flex-wrap">
                    <div className="flex items-center gap-2 min-w-0 flex-wrap">
                      <Server className="w-4 h-4 text-blue-400 flex-shrink-0" />
                      {link.server_name && <span className="text-sm text-white font-medium">{link.server_name}</span>}
                      {link.fqdn && (
                        <span className="text-xs font-mono px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-300">
                          {link.fqdn}
                        </span>
                      )}
                      {link.cluster_id && (
                        <span className="text-xs font-mono px-1.5 py-0.5 rounded bg-violet-500/10 text-violet-300">
                          cluster: {link.cluster_id}
                        </span>
                      )}
                      {link.notes && (
                        <span className="text-[11px] text-glass-500 italic truncate max-w-[240px]" title={link.notes}>
                          {link.notes}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => void toggleReverse(link)}
                        className="glass-button-secondary !p-1.5"
                        title="Find other events/projects on this server"
                      >
                        {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                      </button>
                      {perms.canCreate && (
                        <>
                          <button
                            onClick={() => {
                              setEditingId(link.id);
                              setEditFqdn(link.fqdn ?? '');
                              setEditCluster(link.cluster_id ?? '');
                              setEditServerName(link.server_name ?? '');
                              setEditNotes(link.notes ?? '');
                            }}
                            disabled={busy}
                            className="glass-button-secondary !p-1.5"
                            title="Edit"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => void handleDelete(link)}
                            disabled={busy}
                            className="glass-button-secondary !p-1.5 text-red-300"
                            title="Delete"
                          >
                            {busy ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {expanded && !isEditing && (
                  <div className="px-3 pb-2 border-t border-glass-200/10 pt-2">
                    <p className="text-[11px] text-glass-500 mb-1">
                      Other events/projects on {link.fqdn || `cluster ${link.cluster_id}`}:
                    </p>
                    {reverseLoading ? (
                      <Loader className="w-4 h-4 animate-spin text-glass-400" />
                    ) : !reverse || reverse.filter((r) => r.target_id !== targetId).length === 0 ? (
                      <p className="text-xs text-glass-500">Nothing else is linked to this server.</p>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {reverse
                          .filter((r) => !(r.target_id === targetId && r.target_type === targetType))
                          .map((r) => (
                            <button
                              key={r.id}
                              onClick={() =>
                                navigate(
                                  r.target_type === 'event'
                                    ? `/event-team/events/${r.target_id}`
                                    : `/event-team/projects/${r.target_id}`,
                                )
                              }
                              className={cn(
                                'text-[11px] px-2 py-0.5 rounded-full border text-glass-300 border-glass-200/20',
                                'hover:text-white hover:border-glass-200/40 transition-colors',
                              )}
                            >
                              {r.target_type === 'event' ? '🎉' : '📁'} {r.server_name || r.target_id}
                            </button>
                          ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
